
import { Component, Vue } from 'vue-property-decorator'
import Tracking from "@/types/tracking/tracking"
import { TimesService } from "@/services/timetrack/TimesService"
import ExpenditureList from "@/components/Expenditure/ExpenditureList.vue"
import ExpenditureDialog from "@/components/Expenditure/ExpenditureDialog.vue"
import UpdateTracking from "@/components/Tracking/UpdateTracking.vue"
import { DateAndTimeHelper } from "@/helpers/DateAndTimeHelper"
import TimeDialog from "@/components/DateAndTime/TimeDialog.vue"
import DateDialog from "@/components/DateAndTime/DateDialog.vue"
import DateEndDialog from "@/components/DateAndTime/DateEndDialog.vue"
import TimeEndDialog from "@/components/DateAndTime/TimeEndDialog.vue"
import Project from "@/types/project/project"
import { LocaleMessage } from "vue-i18n"
import { ProjectService } from "@/services/timetrack/ProjectService"
import TrackingFieldsFinishedTracking from "@/components/Tracking/TrackingFieldsFinishedTracking.vue"
import ConfirmDialog from "@/components/ConfirmDialog.vue"
import { ExpenditureService } from "@/services/timetrack/ExpenditureService"

@Component({
  filters: {
  },
  components: {
    TrackingFieldsFinishedTracking,
    UpdateTracking,
    ExpenditureDialog,
    ExpenditureList,
    DateDialog,
    TimeDialog,
    DateEndDialog,
    TimeEndDialog,
    ConfirmDialog
  }
})
export default class TrackingView extends Vue {
  private loading = false
  private dateDialog = false
  private dateEndDialog = false
  private timeDialog = false
  private timeEndDialog = false
  private removeTrackingDialog = false
  private projects: Project[] | null = null
  private tasks: string[] | LocaleMessage[] = ["Beispiel Aufgabe"]
  private dateAndTimeHelper = new DateAndTimeHelper()
  private timeService = new TimesService()
  private expenditureService = new ExpenditureService()
  private projectService = new ProjectService()
  private tracking: Tracking | null = this.$store.getters['tracking/tracking']

  created () {
    if (!this.tracking) {
      this.fetchTracking(this.$route.params.id)
    }
    this.fetchProjects()
    this.fetchTasks()
  }

  private get trackedTime () {
    if (!this.tracking || !this.tracking.startingTime || !this.tracking.endTime) return
    return this.dateAndTimeHelper.getTrackedTime(this.tracking.startingTime, Number(this.tracking.endTime))
  }

  private fetchProjects () {
    this.loading = true
    this.projectService.getAllProjects()
      .then((projects) => {
        if (projects) {
          this.projects = projects as Project[]
        }
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.loading = false
      })
  }

  private fetchTasks () {
    this.loading = true
    this.timeService.getAllTasks()
      .then((tasks) => {
        if (tasks) {
          this.tasks = tasks
        }
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.loading = false
      })
  }

  private fetchTracking (trackingId?: string) {
    if (!trackingId) trackingId = this.tracking?.id || undefined
    if (!trackingId) return
    this.timeService.getTracking(trackingId)
      .then((tracking) => {
        this.tracking = tracking || null
        this.calculateMaking()
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      })
  }

  private updateStartingDate (startingDate: number) {
    if (!this.tracking || !this.tracking.startingTime) return
    const newDate = this.dateAndTimeHelper.getNewMillisecondsDate(this.tracking.startingTime, startingDate)
    this.dateOrTimeChanged(newDate)
    this.dateDialog = false
  }

  private updateStartingTime (startingTime: string) {
    if (!this.tracking || !this.tracking.startingTime) return
    const startedAtDateAndTime = this.dateAndTimeHelper.getMillisecondsFromTimeValue(this.tracking.startingTime, startingTime)
    this.dateOrTimeChanged(startedAtDateAndTime)
    this.timeDialog = false
  }

  private updateEndingDate (endingDate: number) {
    if (!this.tracking || !this.tracking.endTime) return
    const newDate = this.dateAndTimeHelper.getNewMillisecondsDate(Number(this.tracking.endTime), endingDate)
    this.endDateOrTimeChanged(newDate)
    this.dateEndDialog = false
  }

  private updateEndingTime (endingTime: string) {
    if (!this.tracking || !this.tracking.endTime) return
    const endedAtDateAndTime = this.dateAndTimeHelper.getMillisecondsFromTimeValue(Number(this.tracking.endTime), endingTime)
    this.endDateOrTimeChanged(endedAtDateAndTime)
    this.timeEndDialog = false
  }

  private dateOrTimeChanged (date: Date) {
    if (!this.tracking || !this.tracking.id || !date) {
      return
    }
    this.loading = true
    this.timeService.updateDateTime(this.tracking.id, date.getTime())
      .then(() => {
        this.fetchTracking()
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.loading = false
      })
  }

  private endDateOrTimeChanged (date: Date) {
    if (!this.tracking || !this.tracking.id || !date) {
      return
    }
    this.loading = true
    this.timeService.updateEndingDateTime(this.tracking.id, date.getTime())
      .then(() => {
        if (!this.tracking || !this.tracking.id) return
        this.fetchTracking()
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.loading = false
      })
  }

  private calculateMaking () {
    if (!this.tracking || !this.tracking.projectId) return
    this.projectService.getProject(this.tracking.projectId)
      .then((project) => {
        if (project && project.salary && this.tracking && this.tracking.startingTime && this.tracking.endTime) {
          const projectSalary = project.salary
          const difference = Math.abs(this.tracking.endTime - this.tracking.startingTime)
          const making = Number((((projectSalary / 60) / 60) * (difference / 1000)).toFixed(1))
          if (making !== this.tracking.making && this.tracking && this.tracking.id) {
            this.timeService.updateMaking(this.tracking.id, Number(making))
              .then(() => {
                this.fetchTracking()
              })
          }
        }
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.loading = false
      })
  }

  private deleteTracking () {
    if (!this.tracking || !this.tracking.id) return
    this.loading = true
    this.expenditureService.removeExpendituresForTracking(this.tracking.id)
      .then(() => {
        if (!this.tracking || !this.tracking.id) return
        this.timeService.deleteTracking(this.tracking.id)
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.loading = false
        this.$router.back()
      })
  }
}
